/**
 * @file Contains the theme of the entire application, it is the base container.
 */
import ReactDOM from 'react-dom/client';

import { deepmerge } from '@mui/utils';
import { createTheme } from '@mui/material/styles';

import Root from './root';

import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';

import './styles.css';
import './reset.css';
import { urlState } from './state';

const theme = createTheme({
  custom: {
    // Display the top title and variable selection block
    displayTitleBlock: true,
    // Under this value, switch to a vertical layout
    verticalLayoutBreakpoint: 930,
    // Under this value, prefer vertical positioning for inputs (if we are not in the vertical layout)
    verticalInputsBreakpoint: 1512,
    preferredHeight: '100dvh',
  },
  palette: {
    primary: {
      light: '#bfbfbf',
      main: '#B0B0B0',
      dark: '#7b7b7b',
      contrastText: '#fff',
    },
    secondary: {
      light: '#bfbfbf',
      main: '#B0B0B0',
      dark: '#7b7b7b',
      contrastText: '#fff',
    },
    action: {
      light: '#8bb5f9',
      main: '#6092e2',
      dark: '#3d70c0',
      contrastText: '#fff',
    },
  },
  typography: {
    subtitle1: {
      fontSize: '1rem',
      fontFamily: "'Gill Sans','Gill Sans MT',Calibri,'Trebuchet MS',sans-serif;",
    },
    h4: {
      fontSize: '1rem',
      marginTop: '5px',
      fontWeight: 500,
      '@media only screen and (min-device-width: 200px) and (max-device-width: 900px)': {
        marginTop: '5px',
        fontSize: '0.8rem',
      },
    },
    description: {
      '@media only screen and (min-device-width: 200px) and (max-device-width: 900px)': {
        marginBottom: '10px',
        fontSize: '0.8rem',
      },
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          width: '40%',
          borderRadius: '5px',
          fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
          '@media only screen and (min-device-width: 200px) and (max-device-width: 900px)': {
            fontSize: '0.8rem',
            height: '16px',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontFamily: "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif",
          '@media only screen and (min-device-width: 200px) and (max-device-width: 600px)': {
            fontSize: '0.7rem',
            height: '2rem',
          },
          '@media only screen and (min-device-width: 600px) and (max-device-width: 900px)': {
            fontSize: '0.9rem',
            height: '2rem',
          },
        },
      },
    },
    MuiOutlinedInput: {
      defaultProps: {
        sx: {
          fontSize: '13px',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
      div > .ol-control button, div > .layer-switcher.shown.layer-switcher-activation-mode-click > button, div > .layer-switcher:not(.shown) button{
        background-color: #969494;
        color:white;
      }
      div > .layer-switcher.shown.layer-switcher-activation-mode-click > button:hover, div > .layer-switcher:not(.shown) button:hover {
        background-color: #817f7f !important;
        cursor: pointer;
        color:white;
      }
      input[type='radio'] {
        accent-color: dodgerblue;
        height: 1.5rem !important;
      }
    `,
    },
  },
});

const eeaTheme = deepmerge(theme, {
  palette: {
    primary: {
      light: '#47B3FF',
      main: '#004B7F',
      dark: '#003052',
      contrastText: '#fff',
    },
    secondary: {
      light: '#A0E5DC',
      main: '#007B6C',
      dark: '#005248',
      contrastText: '#fff',
    },
    action: {
      main: '#006BB8',
      hover: '#004B7F',
      visited: '#753AAD',
      active: '#003052',
      focus: '#004B7F',
      contrastText: '#fff',
    },
    text: {
      primary: '#3D5265',
    },
    error: { main: '#B83230' },
    warning: { main: '#FF9933' },
    info: { main: '#007B6C' },
    success: { main: '#004B7F' },
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#004B7F',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          display: 'table',
          '&.Mui-selected': {
            color: '#000000',
            backgroundColor: '#A0D7FF',
          },
          '&:hover': {
            color: '#000000',
            backgroundColor: '#a0d7ff45',
          },
          '&.Mui-selected:hover': {
            color: '#000000',
            backgroundColor: '#86c8f7',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
      div > .ol-control button, div > .layer-switcher.shown.layer-switcher-activation-mode-click > button, div > .layer-switcher:not(.shown) button{
        background-color: #004B7F;
        color:white;
      }
      div > .layer-switcher.shown.layer-switcher-activation-mode-click > button:hover, div > .layer-switcher:not(.shown) button:hover {
        background-color: #006BB8 !important;
        cursor: pointer;
      }
      input[type='radio']{
        accent-color: #004B7F;
        height: 1.5rem !important;
      }
    `,
    },
  },
});

const c3sTheme = deepmerge(theme, {
  palette: {
    primary: {
      light: '#e87d78',
      main: '#941333',
      dark: '#951334',
      contrastText: '#fff',
    },
    action: {
      light: '#e87d78',
      main: '#941333',
      dark: '#951334',
      contrastText: '#fff',
    },
    secondary: {
      light: '#e87d78',
      main: '#941333',
      dark: '#951334',
      contrastText: '#fff',
    },
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#e87d789c',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: '#000000',
            backgroundColor: '#94123340',
          },
          '&:hover': {
            color: '#000000',
            backgroundColor: '#9412331c',
          },
          '&.Mui-selected:hover': {
            color: '#000000',
            backgroundColor: '#8d0b2c4a',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
      div > .ol-control button, div > .layer-switcher.shown.layer-switcher-activation-mode-click > button, div > .layer-switcher:not(.shown) button{
        background-color: #941333;
        color:white;
      }
      div > .layer-switcher.shown.layer-switcher-activation-mode-click > button:hover, div > .layer-switcher:not(.shown) button:hover {
        background-color: #72152c !important;
        cursor: pointer;
      }
      input[type='radio'] {
        accent-color: #941333;
        height: 1.5rem !important;
      }
    `,
    },
  },
});

const adaptationDashboardTheme = deepmerge(theme, {
  custom: {
    verticalLayoutBreakpoint: 930,
  },
  palette: {
    primary: {
      light: 'rgb(33, 227, 88)',
      main: 'rgb(17, 136, 51)',
      dark: 'rgb(12, 95, 36)',
      contrastText: 'rgb(230, 237, 242)',
    },
    action: {
      light: 'rgb(33, 227, 88)',
      main: 'rgb(17, 136, 51)',
      dark: 'rgb(12, 95, 36)',
      contrastText: 'rgb(230, 237, 242)',
    },
    secondary: {
      light: 'rgb(33, 227, 88)',
      main: 'rgb(17, 136, 51)',
      dark: 'rgb(12, 95, 36)',
      contrastText: 'rgb(230, 237, 242)',
    },
  },
  components: {
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: 'rgb(0, 94, 202)',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            color: 'white',
            backgroundColor: 'rgb(12, 95, 36)',
          },
          '&:hover': {
            color: 'rgb(33, 227, 88)',
            backgroundColor: 'rgb(12, 95, 36)',
          },
          '&.Mui-selected:hover': {
            color: 'rgb(33, 227, 88)',
            backgroundColor: 'rgb(12, 95, 36)',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
      div > .ol-control button, div > .layer-switcher.shown.layer-switcher-activation-mode-click > button, div > .layer-switcher:not(.shown) button{
        background-color: rgb(17, 136, 51);
        color:white;
      }
      div > .layer-switcher.shown.layer-switcher-activation-mode-click > button:hover, div > .layer-switcher:not(.shown) button:hover {
        background-color: rgb(12, 95, 36) !important;
        cursor: pointer;
      }
      input[type='radio'] {
        accent-color: rgb(17, 136, 51);
        height: 1.5rem !important;
      }
    `,
    },
  },
});

export const themes = {
  base: theme,
  eea: eeaTheme,
  c3s: c3sTheme,
  adaptationDashboard: adaptationDashboardTheme,
};

export function render(root, options = {}) {
  const { theme: customTheme = {}, ...rest } = options;
  let usedTheme = theme;
  if (urlState.theme && urlState.theme in themes) {
    usedTheme = themes[urlState.theme];
  }

  const appTheme = createTheme(deepmerge(usedTheme, customTheme));
  ReactDOM.createRoot(root).render(<Root theme={appTheme} options={rest} />);
}
